import { Currency, FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import { ComponentProps } from 'react';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';

type AccountSelectItemProps = {
  balance: number;
  currency: Currency;
  institutionLogo?: string;
  name: string;
  fspAdapter?: FinancialServiceProviderAdapters | null;
};

export const AccountSelectItem = ({ balance, currency, institutionLogo, name, fspAdapter }: AccountSelectItemProps) => {
  const accountBalance = getAmountWithCurrency({ amount: balance, currency, isAccountBalance: true });
  const description = getSelectItemDescription(fspAdapter);

  return (
    <Inline justify="between" wrap="wrap" className="w-full gap-y-2">
      <Inline space={2} wrap="nowrap">
        {institutionLogo && (
          <div className="account-logo h-9 w-9 rounded-full">
            <img
              src={institutionLogo}
              alt=""
              loading="eager"
              fetchPriority="high"
              className="h-full w-full object-cover"
            />
          </div>
        )}

        <Stack>
          <p className="text-base font-medium text-black" aria-label={`Account name is ${name}`}>
            {name}
          </p>

          <p className="account-type text-xs font-medium text-seapoint-gray" aria-label={`This is a ${description}`}>
            {description}
          </p>
        </Stack>
      </Inline>

      <p className="text-sm font-medium text-seapoint-gray" aria-label={`Balance is ${balance}`}>
        {accountBalance}
      </p>
    </Inline>
  );
};

const getSelectItemDescription = (fspAdapter: ComponentProps<typeof AccountSelectItem>['fspAdapter']) => {
  if (!fspAdapter) {
    return '';
  }

  switch (fspAdapter) {
    case FinancialServiceProviderAdapters.GOCARDLESS:
    case FinancialServiceProviderAdapters.YAPILY:
      return 'Open banking account';
    case FinancialServiceProviderAdapters.MODULR:
      return 'Seapoint account';
    case FinancialServiceProviderAdapters.GOCARDLESS_DEMO_DATA:
      return 'Demo data account';
    default:
      return exhaustiveCheck(fspAdapter);
  }
};
